import {faAt, faFax, faMobile, faPhone, faTimes} from '@fortawesome/pro-light-svg-icons'
import {lazy, Suspense} from 'react'

import {twMerge} from '@sdox/utils'
import {
  FaIcon, LazyImage,
  LazyImportPlayer,
  List,
  ListItem,
  Loading,
} from '@sdox/ui'
import {useCurrentContactPerson} from '@sdox/api'

import type {SalutationTypeEnum} from '@sdox/api'

const Player = lazy(() => LazyImportPlayer)

export interface CustomerContentProps {
  message?: string
  handleDisclose?: () => void
  salutationType?: SalutationTypeEnum
  className?: string
}

export const CustomerContent = ({
  className,
  handleDisclose,
}: CustomerContentProps) => {
  const contactPerson = useCurrentContactPerson()

  if (!contactPerson) {
    return null
  }

  const {video, thumbnail, fullName, email, displayEmail, phone, mobile, fax} = contactPerson
  const shownEmail = displayEmail || email

  return (
    <div className={className}>
      {/* Icon is needed for mobile slide in <ContactSlideIn /> */}
      {typeof handleDisclose === 'function' && (
        <FaIcon
          icon={faTimes}
          className={'absolute right-4 top-4 size-8 cursor-pointer sm:hidden'}
          onClick={handleDisclose}
        />
      )}
      <div
        className={twMerge(
          'grid gap-4 sm:gap-8',
          video && 'sm:grid-cols-2',
        )}
      >
        <div>
          <div className={'flex items-center gap-spacing-12 pb-spacing-16 pr-spacing-24'}>
            <LazyImage
              className={'inline-block size-spacing-80 rounded-full'}
              src={thumbnail ?? undefined}
              alt={'avatar'}
            />
            <span className={'flex flex-col'}>
              <span className={'truncate text-p1-emphasized'}>{fullName}</span>
              {contactPerson.title ? (
                <span className={'-mt-spacing-2 truncate text-glyph-default-secondary'}>{contactPerson.title}</span>
              ) : null}
            </span>
          </div>
          <List
            lined
            className={'mb-spacing-16'}
          >
            {phone && (
              <ListItem icon={<FaIcon icon={faPhone} />}>
                <a
                  href={`tel:${phone}`}
                  className={'text-sm focus:outline-none'}
                >
                  {phone}
                </a>
              </ListItem>
            )}
            {mobile && (
              <ListItem icon={<FaIcon icon={faMobile} />}>
                <a
                  href={`tel:${mobile}`}
                  className={'text-sm focus:outline-none'}
                >
                  {mobile}
                </a>
              </ListItem>
            )}
            {fax && (
              <ListItem icon={<FaIcon icon={faFax} />}>
                <a
                  href={`tel:${fax}`}
                  className={'text-sm focus:outline-none'}
                >
                  {fax}
                </a>
              </ListItem>
            )}
            {shownEmail && (
              <ListItem icon={<FaIcon icon={faAt} />}>
                <a
                  href={`mailto:${shownEmail}`}
                  className={'text-sm focus:outline-none'}
                >
                  {shownEmail}
                </a>
              </ListItem>
            )}
          </List>
        </div>
        {video && (
          <div className={'flex items-center justify-center'}>
            <Suspense fallback={<Loading />}>
              <Player
                url={video.src}
                stopOnLeave
                thumbnail={video.thumbnail ?? undefined}
                className={'rounded-radius-md'}
              />
            </Suspense>
          </div>
        )}
      </div>
    </div>
  )
}
